import debounceEvent from '@redux-beacon/debounce-event';
import { createMiddleware } from 'redux-beacon';
import { GoogleTagManager } from 'middlewares/googleTagManager';
import { addMiddleware } from 'redux-dynamic-middlewares';
import GoogleAnalytics4EventType from 'enums/googleAnalytics4EventType';
import getGoogleAnalytics4Event from 'services/googleAnalytics4EventBuilder';
import { googleAnalytics4Event, googleAnalytics4Impression } from './actions';

/**
 * Dispatches a Google Analytics 4 event with optional debouncing.
 * 
 * @param {string} eventType - The type of event to dispatch
 * @param {Object} payload - The event payload
 * @param {boolean} [payload.skipDebounce] - If true, bypasses debouncing and dispatches the event immediately
 * @param {string} payload.eventName - The name of the Google Analytics event
 * @param {Object} [payload.customProperties] - Custom properties to include with the event
 * @returns {*} The result of getGoogleAnalytics4Event, either directly or wrapped in debounceEvent
 */
const dispatchEvent = (eventType, payload) => {
  const { skipDebounce, customProperties, eventName } = payload;
  const event = getGoogleAnalytics4Event(eventType, { eventName, customProperties });

  if (skipDebounce) {
    return event;
  }
  return debounceEvent(1000, event);
};

/**
 * The eventsMap function is a mapping of Redux actions to GoogleAnalytics4 events.
 * It returns an array of GoogleAnalytics4 events, which are objects with the following shape:
 * {
 *   eventType: string, // The name of the event (e.g., 'track')
 *   fieldsObj: object, // An object containing all properties for this event type (e.g., {eventName})
 * }
 *
 * @param action Determine which event to return
 *
 * @return An array of events
 */
const eventsMap = action => {
  switch (action.type) {
    case googleAnalytics4Event.type: {
      const payload = action.payload;
      const event = dispatchEvent(GoogleAnalytics4EventType.Event, payload);
      return event;
    }
    case googleAnalytics4Impression.type: {
      const payload = action.payload;
      const event = dispatchEvent(GoogleAnalytics4EventType.Impression, payload);
      return event;
    }
    default:
      return [];
  }
};

const googleTagManager = new GoogleTagManager();

/* `const middleware = createMiddleware(eventsMap, GoogleTagManager);` is creating a middleware using
`redux-beacon` library. The `createMiddleware` function takes two arguments: `eventsMap` and
`googleTagManager`. `eventsMap` is a function that maps Redux actions to G4 events, and `googleTagManager` is an
instance of the `@redux-beacon` library. The middleware created by `createMiddleware` will
listen for Redux actions and send corresponding G4 events using the `googleTagManager` instance. */
const middleware = createMiddleware(eventsMap, googleTagManager.getTarget());

// registers the middleware in redux
/* `addMiddleware(middleware)` is registering the middleware created by `createMiddleware` in the Redux
store. This allows the middleware to listen for Redux actions and send corresponding G4 events
using the `googleTagManager` instance. */
addMiddleware(middleware);
